import { Link } from 'react-router-dom';
import InfoPanel from '../../reusable/InfoPanel/InfoPanel';
import InfoRow from '../../reusable/InfoPanel/InfoRow';
import { CHANNELS, FAQS, INTRO, TRADING_RULES } from './data';
import { t } from '../../i18n/i18n';
import './HelpCentre.scss';

/**
 * One question and its answer.
 *
 * An entry that names a dashboard page is the whole row a link to it — the
 * answer to "where are my credentials?" is a place, and making the reader hunt
 * for it after being told where it is would be the wrong end of the job. The
 * rest are plain text, since there is nowhere for them to go.
 *
 * The hit area bleeds past the text on both sides so the hover highlight reads
 * as a row of the card rather than a box drawn around a paragraph.
 */
/**
 * The "goes somewhere" mark, in its own slot at the end of the row rather than
 * trailing the question text — so it stays put against the top-right of the
 * entry however many lines the answer runs to.
 *
 * One shape for both destinations. An in-app page and an article are told apart
 * by where they land, not by two arrows the reader has to learn.
 */
function GoArrow() {
  return (
    <span className="hc-qa__go" aria-hidden="true">
      <svg viewBox="0 0 12 12" fill="none">
        <path
          d="M3.5 8.5 8.5 3.5M4.75 3.5H8.5V7.25"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function Qa({ question, answer, to, href }) {
  const body = (
    <>
      <span className="hc-qa__copy">
        <span className="hc-qa__q">{t(question)}</span>
        <span className="hc-qa__a">{t(answer)}</span>
      </span>
      <GoArrow />
    </>
  );

  return (
    <li className="hc-qa">
      {to && (
        <Link className="hc-qa__hit is-link" to={to}>
          {body}
        </Link>
      )}

      {/* An article, so it leaves the app — new tab, because losing the
          dashboard to read a rule is not a fair trade. */}
      {!to && href && (
        <a
          className="hc-qa__hit is-link"
          href={href}
          target="_blank"
          rel="noreferrer noopener"
        >
          {body}
        </a>
      )}

      {!to && !href && (
        <div className="hc-qa__hit">
          <span className="hc-qa__copy">
            <span className="hc-qa__q">{t(question)}</span>
            <span className="hc-qa__a">{t(answer)}</span>
          </span>
        </div>
      )}
    </li>
  );
}

/**
 * Help Centre — 1:20683.
 *
 * Two columns of questions over one card of ways to reach a person. The card,
 * the raised channel rows and the page furniture come from
 * `src/reusable/InfoPanel/`; the question list is this page's own.
 */
export default function HelpCentre() {
  return (
    <div className="help-centre">
      <header className="page-head">
        <h1 className="page-head__title">{t('Help centre')}</h1>
        <p className="page-head__meta">{t(INTRO)}</p>
      </header>

      <div className="page-two">
        <InfoPanel title={t('Trading rules')}>
          <ul className="hc-qas">
            {TRADING_RULES.map((item) => (
              <Qa key={item.id} {...item} />
            ))}
          </ul>
        </InfoPanel>

        <InfoPanel title={t('Frequently asked')}>
          <ul className="hc-qas">
            {FAQS.map((item) => (
              <Qa key={item.id} {...item} />
            ))}
          </ul>
        </InfoPanel>
      </div>

      <InfoPanel title={t('Still stuck')}>
        <div className="ip-rows">
          {CHANNELS.map((channel) => (
            <InfoRow
              key={channel.id}
              logo={channel.logo}
              label={t(channel.label)}
              note={t(channel.note)}
              action={t(channel.action)}
              href={channel.href}
            />
          ))}
        </div>
      </InfoPanel>
    </div>
  );
}
